import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { getTestIds } from 'seagull/utils/TestIds';
import { AiWorkingOnIt } from '~/components/ai/AiWorkingOnIt/AiWorkingOnIt';
import { useLocaleKeys } from '~/i18n';
import { AppliedMessage } from '../messages/AppliedMessage';
import { ChatTextMessage } from '../messages/ChatTextMessage';
import { ClarifyingQuestionMessage } from '../messages/ClarifyingQuestionMessage';
import { ConfirmationHierarchyMessage } from '../messages/ConfirmationHierarchyMessage';
import { ConfirmationSummaryMessage } from '../messages/ConfirmationSummaryMessage';
import { DismissedMessage } from '../messages/DismissedMessage';
import { InviteMessage } from '../messages/InviteMessage';
import { ListSelectMessage } from '../messages/ListSelectMessage';
import { ListSelectMultiMessage } from '../messages/ListSelectMultiMessage';
import { OutOfScopeMessage } from '../messages/OutOfScopeMessage';
import { SimpleConfirmationMessage } from '../messages/SimpleConfirmationMessage';
import { UserMessage } from '../messages/UserMessage';
import styles from './MessageThread.module.scss';
export const GetMessageThreadTID = (parentTestId)=>getTestIds(parentTestId);
const KNOWN_TOOLS = [
    'list_contacts',
    'list_service_templates',
    'list_legal_terms',
    'list_proposal_templates',
    'get_draft',
    'list_agreements',
    'get_agreement',
    'list_proposals',
    'get_proposal',
    'get_proposal_template',
    'get_contact',
    'route_request'
];
const isKnownTool = (tool)=>KNOWN_TOOLS.includes(tool);
const renderAssistantContent = (content, messageId, isInteractive, props)=>{
    const submitFeedback = isInteractive ? (rating, comment)=>props.onSubmitFeedback(messageId, rating, comment) : undefined;
    const dismissFeedback = isInteractive ? ()=>props.onDismissFeedback(messageId) : undefined;
    switch(content.type){
        case 'invite':
            return /*#__PURE__*/ _jsx(InviteMessage, {
                content: content,
                messageId: messageId,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'confirmation-summary':
            return /*#__PURE__*/ _jsx(ConfirmationSummaryMessage, {
                content: content,
                messageId: messageId,
                interactive: isInteractive,
                onDismiss: props.onDismissProposal,
                onApply: props.onApplyProposal,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'confirmation-hierarchy':
            return /*#__PURE__*/ _jsx(ConfirmationHierarchyMessage, {
                content: content,
                messageId: messageId,
                interactive: isInteractive,
                onDismiss: props.onDismissProposal,
                onApply: props.onApplyProposal,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'simple-confirmation':
            return /*#__PURE__*/ _jsx(SimpleConfirmationMessage, {
                content: content,
                messageId: messageId,
                interactive: isInteractive,
                onDismiss: props.onDismissProposal,
                onApply: props.onApplyProposal,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'clarifying-question':
            return /*#__PURE__*/ _jsx(ClarifyingQuestionMessage, {
                content: content,
                messageId: messageId,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'list-select':
            return /*#__PURE__*/ _jsx(ListSelectMessage, {
                content: content,
                messageId: messageId,
                onListSelectClick: isInteractive ? props.onListSelectClick : undefined
            });
        case 'list-select-multi':
            return /*#__PURE__*/ _jsx(ListSelectMultiMessage, {
                content: content,
                messageId: messageId,
                interactive: isInteractive,
                onDismiss: props.onDismissProposal,
                onApply: props.onApplyProposal
            });
        case 'applied':
            return /*#__PURE__*/ _jsx(AppliedMessage, {
                content: content,
                messageId: messageId,
                showFollowUpSuggestions: isInteractive,
                onSuggestionClick: props.onSuggestionClick,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'dismissed':
            return /*#__PURE__*/ _jsx(DismissedMessage, {
                content: content,
                messageId: messageId,
                showFollowUpSuggestions: isInteractive,
                onSuggestionClick: props.onSuggestionClick,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'out-of-scope':
            return /*#__PURE__*/ _jsx(OutOfScopeMessage, {
                content: content,
                messageId: messageId,
                onSubmitFeedback: submitFeedback,
                onDismissFeedback: dismissFeedback
            });
        case 'chat-text':
            return /*#__PURE__*/ _jsx(ChatTextMessage, {
                content: content,
                messageId: messageId
            });
    }
};
export const MessageThread = (props)=>{
    var _ref;
    var _activeToolCalls_find;
    const { messages, isThinking, activeToolCalls, thinkingText, thinkingLabel, error, testId } = props;
    const { t } = useLocaleKeys();
    const { MessageThreadTID } = useMemo(()=>GetMessageThreadTID(testId), [
        testId
    ]);
    const activeTool = (_activeToolCalls_find = activeToolCalls.find((tc)=>tc.durationMs == null)) === null || _activeToolCalls_find === void 0 ? void 0 : _activeToolCalls_find.tool;
    const activeToolLabel = activeTool != null && isKnownTool(activeTool) ? t.agenticDrafting.messages.toolChips[activeTool]() : undefined;
    const workingLabel = (_ref = activeToolLabel !== null && activeToolLabel !== void 0 ? activeToolLabel : thinkingLabel) !== null && _ref !== void 0 ? _ref : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": MessageThreadTID.Container(),
        className: styles.container,
        children: [
            messages.map((message, idx)=>{
                if (message.role === 'user') {
                    return /*#__PURE__*/ _jsx(UserMessage, {
                        content: message.content,
                        messageId: message.id
                    }, message.id);
                }
                const isInteractive = idx === messages.length - 1;
                return /*#__PURE__*/ _jsx("div", {
                    "data-testid": MessageThreadTID.AssistantMessage(message.id),
                    className: styles.assistantMessage,
                    children: renderAssistantContent(message.content, message.id, isInteractive, props)
                }, message.id);
            }),
            isThinking && /*#__PURE__*/ _jsx(AiWorkingOnIt, {
                label: workingLabel,
                testId: MessageThreadTID.ThinkingIndicator()
            }),
            isThinking && thinkingText != null && /*#__PURE__*/ _jsx("div", {
                "data-testid": MessageThreadTID.ThinkingLine(),
                className: styles.thinkingLine,
                children: thinkingText
            }),
            error != null && /*#__PURE__*/ _jsx("div", {
                "data-testid": MessageThreadTID.Error(),
                className: styles.error,
                children: error
            })
        ]
    });
};
